import axios from "axios";
import type { AxiosInstance } from "axios";
import { API_ROUTES } from "@/constants/apiRoutes";
import { api } from "@/services/apiClient";
import {
  createOtpRequiredError,
  isOtpChallengeResponse,
} from "@/services/apiErrors";
import {
  createOtpHeaders,
  FORM_HTTP_HEADERS,
  JSON_HTTP_HEADERS,
} from "@/services/httpHeaders";
import {
  createIamUserPayload,
  createPasswordLoginGrantPayload,
} from "@/services/iamPayloads";
import type { MeResponse, RegisterPayload, TokenResponse, User } from "@/types";

export interface ApiRequestOptions {
  signal?: AbortSignal;
  // Sent as they are: a request that speaks for another project carries the
  // access token of that project rather than the session's.
  headers?: Record<string, string>;
}

export interface LoginRequestOptions extends ApiRequestOptions {
  otpCode?: string;
}

interface AuthProfile {
  user: User;
  projectId: string | null;
}

interface IntrospectionResponse {
  permissions: string[];
  projectId: string | null;
  user: User;
}

export async function registerUser(
  payload: RegisterPayload,
  options: ApiRequestOptions = {},
): Promise<User> {
  const { data } = await api.post<User>(
    API_ROUTES.iam.users,
    createIamUserPayload(payload),
    options,
  );
  return data;
}

export async function loginUser(
  login: string,
  password: string,
  options: LoginRequestOptions = {},
): Promise<TokenResponse> {
  try {
    const { data } = await axios.post<TokenResponse>(
      API_ROUTES.iam.token,
      createPasswordLoginGrantPayload(login, password),
      {
        headers: {
          ...FORM_HTTP_HEADERS,
          ...createOtpHeaders(options.otpCode),
        },
        signal: options.signal,
      },
    );
    return data;
  } catch (err) {
    if (
      isOtpChallengeResponse(err, {
        allowInvalidClientFallback: !options.otpCode,
      })
    ) {
      throw createOtpRequiredError(err, "OTP code is required.");
    }

    throw err;
  }
}

export async function logoutUser(
  options: ApiRequestOptions = {},
): Promise<void> {
  await api.post(API_ROUTES.iam.logout, undefined, options);
}

export async function getMeProfile(
  options: ApiRequestOptions = {},
): Promise<AuthProfile> {
  const { data } = await api.get<MeResponse | User>(
    API_ROUTES.iam.me,
    options,
  );
  return normalizeMeResponse(data);
}

export function normalizeIntrospectionResponse(data: unknown): IntrospectionResponse {
  const response = data as {
    permissions?: unknown;
    project_id?: unknown;
    user_info?: User;
  };
  return {
    permissions: Array.isArray(response.permissions)
      ? response.permissions.filter((item): item is string => typeof item === "string")
      : [],
    projectId: typeof response.project_id === "string" ? response.project_id : null,
    user: response.user_info as User,
  };
}

export async function introspectAuthSession(
  options: ApiRequestOptions = {},
  apiOverride?: AxiosInstance,
): Promise<IntrospectionResponse> {
  const client = apiOverride ?? api;
  const { data } = await client.get(API_ROUTES.iam.introspect, options);
  return normalizeIntrospectionResponse(data);
}

export async function confirmUserEmail(
  userId: string,
  code: string,
  options: ApiRequestOptions = {},
): Promise<void> {
  await api.post(API_ROUTES.iam.confirmEmail(userId), { code }, options);
}

function normalizeMeResponse(data: MeResponse | User): AuthProfile {
  if (isMeResponse(data)) {
    return {
      user: data.user,
      projectId: data.project_id ?? null,
    };
  }

  return {
    user: data,
    projectId: null,
  };
}

function isMeResponse(data: MeResponse | User): data is MeResponse {
  return "user" in data && typeof data.user === "object" && data.user !== null;
}

export async function sendResetPasswordCode(
  email: string,
  options: ApiRequestOptions = {},
): Promise<void> {
  await axios.post(
    API_ROUTES.iam.sendResetPasswordCode,
    { email },
    { headers: JSON_HTTP_HEADERS, signal: options.signal },
  );
}

export async function resetUserPassword(
  userId: string,
  newPassword: string,
  code: string,
  options: ApiRequestOptions = {},
): Promise<User> {
  const { data } = await axios.post<User>(
    API_ROUTES.iam.resetPassword(userId),
    { new_password: newPassword, code },
    { headers: JSON_HTTP_HEADERS, signal: options.signal },
  );
  return data;
}
